class CustomLink extends HTMLAnchorElement {
  click(event) {
    event.preventDefault();
    const _url = new URL(this.href, window.origin);
    this.dispatchEvent(new CustomEvent('navigate', {
      bubbles: true,
      detail: _url.href.replace(_url.origin, ''),
    }))
  }
  connectedCallback() {
    this.addEventListener('click', this.click);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.click);
  }

}
customElements.define('navigate-link', CustomLink, { extends: "a" })



class CustomInput extends HTMLInputElement {
  static observedAttributes = ['autocomplete', 'type', 'name', 'pattern', 'customValidity',];
  /**
   * @type {Object.<(string,RegExp)>}
   */
  static PatternMap = {
    phoneNumber: /(\+(9[976]\d|8[987530]\d|6[987]\d|5[90]\d|42\d|3[875]\d|2[98654321]\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1))*\s*[\d\s]*/,
    mobilePhoneNumber: /(\+(9[976]\d|8[987530]\d|6[987]\d|5[90]\d|42\d|3[875]\d|2[98654321]\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1))*\s*[\d\s]*/,
  };
  /**
   * @typedef {('button' | 'checkbox' | 'color' | 'date' | 'datetime-local' | 'email' | 'hidden' | 'image' | 'month' | 'number' | 'password' | 'radio' | 'range' | 'reset' | 'search' | 'submit' | 'tel' | 'text' | 'time' | 'url' | 'week')} InputType
   * @typedef {(Object.<(string,InputType)>)} InputTypeMap
   * @type {InputTypeMap}
   */
  static TypeMap = {
    firstName: 'text',
    lastName: 'text',
    reference: 'text',
    email: 'email',
    organizationName: 'text',
    line1: 'text',
    line2: 'text',
    city: 'text',
    postalCode: 'text',
    regionCode: 'text',
    country: 'text',
    phoneNumber: 'tel',
    mobilePhoneNumber: 'tel',
    note: 'text',
    gln: 'text',
    cvr: 'text',
    username: 'text',
    password: 'password',

  };
  /**
   * @typedef {('on' | 'off' | 'name' | 'honorific-prefix' | 'given-name' | 'additional-name' | 'family-name' | 'honorific-suffix' | 'nickname' | 'email' | 'username' | 'new-password' | 'current-password' | 'one-time-code' | 'organization-title' | 'organization' | 'street-address' | 'address-line1' | 'address-line2' | 'address-line3' | 'address-level4' | 'address-level3' | 'address-level2' | 'address-level1' | 'country' | 'country-name' | 'postal-code' |            'cc-name' | 'cc-given-name' | 'cc-additional-name' | 'cc-family-name' | 'cc-number' | 'cc-exp' | 'cc-exp-month' | 'cc-exp-year' | 'cc-csc' | 'cc-type' |            'transaction-currency' | 'transaction-amount' |            'language' |            'bday' | 'bday-day' | 'bday-month' | 'bday-year' |            'tel' | 'tel-country-code' | 'tel-national' | 'tel-area-code' | 'tel-local' | 'tel-extension' | 'impp' | 'url' | 'webauthn')} InputAutoCompleteOption
   * @typedef {(Object.<(string,InputAutoCompleteOption)>)} InputAutoCompleteOptionMap
   * @type {InputAutoCompleteOptionMap}
   * */
  static AutocompleteMap = {
    firstName: 'given-name',
    reference: 'off',
    lastName: 'family-name',
    email: 'email',
    organizationName: 'organization',
    line1: 'address-line1',
    line2: 'address-line2',
    city: 'address-level2',
    postalCode: 'postal-code',
    regionCode: 'off',
    country: 'country',
    phoneNumber: 'tel',
    mobilePhoneNumber: 'tel',
    note: 'off',
    gln: 'on',
    cvr: 'on',
    username: 'username',
    password: 'current-password'
  };
  /**
   * @type {Array.<(string)>}
   */
  static ErrorTypes = [
    'badInput',
    'customError',
    'patternMismatch',
    'rangeOverflow',
    'rangeUnderflow',
    'stepMismatch',
    'tooLong',
    'tooShort',
    'typeMismatch',
    'valid',
    'valueMissing',
  ]

  connectedCallback() {
    this.addEventListener('input', this.checkValidity());
  }
  disconnectedCallback() {
    this.removeEventListener('input', this.checkValidity());
  }

  /**
   *
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    this.#setName(this.name);
  }
  /**
   *
   * @returns {string}
   */
  #formName() {
    let fieldWithForm = this.closest('fieldset[form]');
    let formElm = fieldWithForm != null ? window[fieldWithForm.getAttribute('form')] : this.closest('form');
    return formElm.name;
  }
  /**
   *
   * @returns {string}
   */
  #fieldsetName() {
    return this.closest('fieldset')?.name ?? '';
  }

  #setLabel() {
    this.#translate([this.#formName(), this.#fieldsetName(), this.name, 'label']).then(text => {
      if (!(text?.length > 0))
        return;
      let oldLabelElm = this.closest('label').querySelector('.label');
      if (oldLabelElm != null)
        oldLabelElm.remove();
      let labelElm = document.createElement('span');
      labelElm.className = 'label';
      labelElm.innerHTML = text;
      this.parentElement.prepend(labelElm);
    })
  }
  /**
   *
   */
  #setPlaceholder() {
    if (this.placeholder?.length > 0)
      this.placeholder = this.placeholder;
    else
      this.#translate([this.#formName(), this.#fieldsetName(), this.name, 'placeholder']).then(text => this.placeholder = text);
  }
  /**
   *
   */
  #setAutocomplete() {
    let fieldsetName = this.#fieldsetName();
    this.autocomplete = this.autocomplete?.length > 0 ? this.autocomplete : [['shipping', 'billing'].includes(fieldsetName) ? fieldsetName : '', CustomInput.AutocompleteMap[this.name] ?? 'off'].join(' ');
  }
  /**
   *
   * @param {string} name
   */
  #setName(name) {
    this.name = name;
    this.#setAutocomplete();
    this.type = this.type?.length > 0 ? this.type : CustomInput.TypeMap[name];
    this.pattern = this.pattern?.length > 0 ? this.pattern : CustomInput.PatternMap[name] ?? null;
    this.#setPlaceholder();
    this.#setLabel();
  }
  /**
   *
   * @param {Array.<(string)>} keys
   * @returns {string}
   */
  #translate(keys) {
    let translationMap = JSON.parse(globalThis?.sessionStorage.getItem('translations'));

    return keys.reduce((acc, key) => acc[key], translationMap);
  }
  /**
   *
   * @returns {void}
   */
  checkValidity() {
    // Custom
    if (this.customValidity != null)
      this.setCustomValidity(this.customValidity(this));

    // Validity
    let validity = this.validity;
    if (this.parentElement.querySelector('.errors') != null)
      this.parentElement.querySelector('.errors').remove();
    if (validity.valid)
      return;
    // Errors
    let errorElm = document.createElement('span');
    errorElm.className = 'errors';
    let formName = this.#formName();
    let fieldsetName = this.#fieldsetName();
    [
      'badInput',
      'customError',
      'patternMismatch',
      'rangeOverflow',
      'rangeUnderflow',
      'stepMismatch',
      'tooLong',
      'tooShort',
      'typeMismatch',
      'valid',
      'valueMissing',
    ].forEach(_validityType => {
      if (validity[_validityType]) {
        let _errorElm = document.createElement('span');
        _errorElm.className = _validityType;
        this.#translate([formName, fieldsetName, this.name, _validityType]).then(_ => _errorElm.innerHTML = _);
        errorElm.append(_errorElm);
      }
    })

    this.parentElement.append(errorElm);
    // this.reportValidity();
  }
}
customElements.define('custom-input', CustomInput, { extends: "input" })
// class DetailsSelect extends HTMLDetailsElement {
//   autoChecked;
//   autoFoucs;
//   /**
//    * @param {MouseEvent} event
//    */
//   static #clicked(event) {
//     if (!(this.contains(event.target) || this == event.target))
//       this.open = false;
//     return;
//   }
//   static #mouseUped(event) {
//     /**
//      * @type {DetailsSelect}
//      */
//     let _this = event.target;
//     if (_this instanceof DetailsSelect)
//       _this.open = false
//   }
//   static #toggled(event) {
//     /**
//      * @type {DetailsSelect}
//      */
//     let _this = event.target;
//     if (_this instanceof DetailsSelect)
//       setTimeout(() => {
//         if (_this.open) {
//           this.querySelector('input').click();
//           if (_this.autoFocus)
//             _this?.querySelector(_this.autoFocus)?.focus();
//           if (_this.autoChecked)
//             _this.querySelector(_this.autoChecked).click();
//         } else {
//           this.parentElement.parentElement.querySelector('input[value=single]').click();
//         }
//       }, 50);
//   }

//   connectedCallback() {
//     document.addEventListener('mouseup', DetailsSelect.#mouseUped);
//     document.addEventListener('toggle', DetailsSelect.#toggled);
//     document.addEventListener('click', DetailsSelect.#clicked);
//     this.autoChecked = this.getAttribute('autoChecked');
//     this.autoFoucs = this.getAttribute('autoFoucs');
//   }

//   disconnectedCallback() {
//     this.removeEventListener('click', DetailsSelect.#clicked);
//   }

//   /**
//    *
//    * @param {string} name
//    * @param {string} oldValue
//    * @param {string} newValue
//    */
//   attributeChangedCallback(name, oldValue, newValue) {
//     if (name == 'autoChecked')
//       this.autoChecked = newValue;
//     if (name = 'autoFoucs')
//       this.autoFoucs = newValue;
//   }
// }
// customElements.define('details-select', DetailsSelect, { extends: "select" })

// @w11k/ngx-componentdestroyed
// @ngx-translate

